import React, { useMemo, useState } from 'react';
import { Box, Typography, Grid, Card, CardMedia, CardContent, IconButton, Button, Checkbox, Dialog } from '@mui/material';
import { LocalOffer as TagIcon, Refresh as RefreshIcon, ZoomOutMap as ZoomOutMapIcon } from '@mui/icons-material';
import { useData } from '../DataProvider';
import { storage, db } from '../../firebase';
import { ref, getDownloadURL, listAll } from 'firebase/storage';
import ImageAnnotation from './ImageAnnotation';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { BEGINNING_MARKER, ENDING_MARKER } from '../constants';

// Component to handle Firebase click image loading
const ClickImageItem = ({ event, appInfo, onSeekToTime, onAnnotationClick, onFullscreenClick, runId = null, allAnnotations = [], isSelected = false, shouldIgnore = false, isManualCapture = false }) => {
  const [imageUrl, setImageUrl] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  // Use pre-computed clickImageFileName from enriched event
  const clickImageFileName = event.clickImageFileName;

  // Check for existing annotations for this specific image
  const imageAnnotations = allAnnotations.filter(ann => ann.Image === clickImageFileName);
  const hasAnnotations = imageAnnotations.length > 0;

  // Format time helper
  const formatTime = (ms) => {
    if (typeof ms !== 'number' || isNaN(ms)) return '';
    const sign = ms < 0 ? '-' : '';
    const absMs = Math.abs(ms);
    const hours = Math.floor(absMs / (1000 * 60 * 60));
    const minutes = Math.floor((absMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((absMs % (1000 * 60)) / 1000);
    const milliseconds = Math.floor(absMs % 1000);
    
    if (hours > 0) {
      return `${sign}${hours}:${pad(minutes)}:${pad(seconds)}.${pad(milliseconds, 3)}`;
    } else {
      return `${sign}${minutes}:${pad(seconds)}.${pad(milliseconds, 3)}`;
    }
  };

  const pad = (n, len = 2) => n.toString().padStart(len, '0');

  React.useEffect(() => {
    let cancelled = false; // Flag to prevent state updates if component unmounts
    
    const loadImage = async () => {
      try {
        if (cancelled) return; // Early return if cancelled
        
        setLoading(true);
        setError(null);
        
        // Early safety check - if appInfo is undefined, don't proceed
        if (!appInfo || !appInfo.clickimagesPath || !clickImageFileName) {
          if (!cancelled) {
            setLoading(false);
            if (!appInfo) {
              setError('App info not available');
            } else if (!appInfo.clickimagesPath) {
              setError('No clickimages path in app info');
            } else {
              setError('No image filename available');
            }
          }
          return;
        }
        
        const firebaseImagePath = `${appInfo.clickimagesPath}/${clickImageFileName}.jpg`;

        try {
          const imageRef = ref(storage, firebaseImagePath);
          const url = await getDownloadURL(imageRef);
          if (url && !cancelled) {
            setImageUrl(url);
          }
        } catch (error) {
          if (cancelled) return; // Don't update state if cancelled
          
          if (error.code === 'storage/object-not-found') {
            setError('Image not yet available');
          } else {
            setError(error.message);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadImage();

    // Cleanup function to cancel ongoing operations
    return () => {
      cancelled = true;
    };
  }, [clickImageFileName, appInfo]); // Simplified dependency array

  if (loading) {
    return (
      <Card sx={{ width: 200 }}>
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
          <Typography variant="body2" color="text.secondary">
            Loading...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (error || !imageUrl) {
    return (
      <Card sx={{ width: 200 }}>
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
          <Typography variant="body2" color="error">
            {error || 'No image available'}
          </Typography>
          <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
            Time: {formatTime(event.VideoOffset)}
          </Typography>
          {event.ElementControlType && (
            <Typography variant="caption" sx={{ display: 'block' }}>
              Control: {event.ElementControlType}
            </Typography>
          )}
          {event.ElementControlName && (
            <Typography variant="caption" sx={{ display: 'block' }}>
              Name: {event.ElementControlName}
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card 
      sx={{ 
        width: 200, 
        cursor: 'pointer', 
        position: 'relative', 
        ...(shouldIgnore ? { bgcolor: 'action.disabledBackground' } : {}),
        ...(isSelected ? { border: '2px solid', borderColor: 'secondary.main' } : {})
      }}
      onClick={() => onSeekToTime(event.VideoOffset / 1000)}
    >
      <Box sx={{ position: 'relative' }}>
        <CardMedia
          component="img"
          height="140"
          image={imageUrl}
          alt={`Click screenshot ${clickImageFileName}`}
          sx={{ objectFit: 'contain' }}
        />
      </Box>
      {/* Fullscreen preview – bottom-left, mirroring the annotation icon on the right */}
      <IconButton
        size="small"
        sx={{
          position: 'absolute',
          bottom: 6,
          left: 6,
          bgcolor: 'background.paper',
          opacity: 0.9,
          '&:hover': { bgcolor: 'background.default', opacity: 1 }
        }}
        onClick={(e) => {
          e.stopPropagation();
          onFullscreenClick?.({ clickImageFileName, imageUrl });
        }}
      >
        <ZoomOutMapIcon fontSize="small" sx={{ color: 'text.primary' }} />
      </IconButton>
      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
        <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'primary.main', display: 'block', lineHeight: 1.3 }}>
          {formatTime(event.VideoOffset)}
        </Typography>
        {isManualCapture ? (
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }}>
            Manual Capture
          </Typography>
        ) : (
          <>
            {event.ElementControlType && (
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {event.ElementControlType}
                {event.ElementControlName && `: ${event.ElementControlName}`}
              </Typography>
            )}
            {event.WindowTitle && (
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontStyle: 'italic', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                "{event.WindowTitle}"
              </Typography>
            )}
          </>
        )}
        {clickImageFileName && (
          <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', fontSize: '0.65rem', lineHeight: 1.3, mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {clickImageFileName}.jpg
          </Typography>
        )}
      </CardContent>
      {runId && (
        <IconButton
          size="small"
          sx={{ 
            position: 'absolute',
            bottom: 6,
            right: 6,
            bgcolor: hasAnnotations ? 'success.main' : 'background.paper',
            opacity: 0.9,
            '&:hover': { 
              bgcolor: hasAnnotations ? 'success.dark' : 'background.default',
              opacity: 1
            }
          }}
          onClick={(e) => {
            e.stopPropagation();
            onAnnotationClick({ event, clickImageFileName, imageUrl });
          }}
        >
          <TagIcon fontSize="small" sx={{ color: hasAnnotations ? 'success.contrastText' : 'text.primary' }} />
        </IconButton>
      )}
    </Card>
  );
};

// Component for virtual Beginning/Ending event items
const VirtualEventItem = ({ event, label, onSeekToTime, onAnnotationClick, runId = null, allAnnotations = [], isSelected = false }) => {
  const clickImageFileName = event.clickImageFileName; // This will be __BEGINNING__ or __ENDING__

  // Check for existing annotations for this virtual item
  const imageAnnotations = allAnnotations.filter(ann => ann.Image === clickImageFileName);
  const hasAnnotations = imageAnnotations.length > 0;

  // Format time helper
  const formatTime = (ms) => {
    if (typeof ms !== 'number' || isNaN(ms)) return '';
    const sign = ms < 0 ? '-' : '';
    const absMs = Math.abs(ms);
    const hours = Math.floor(absMs / (1000 * 60 * 60));
    const minutes = Math.floor((absMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((absMs % (1000 * 60)) / 1000);
    const milliseconds = Math.floor(absMs % 1000);
    
    if (hours > 0) {
      return `${sign}${hours}:${pad(minutes)}:${pad(seconds)}.${pad(milliseconds, 3)}`;
    } else {
      return `${sign}${minutes}:${pad(seconds)}.${pad(milliseconds, 3)}`;
    }
  };

  const pad = (n, len = 2) => n.toString().padStart(len, '0');

  return (
    <Card 
      sx={{ 
        width: 200, 
        cursor: 'pointer', 
        position: 'relative', 
        bgcolor: 'background.default',
        border: '2px dashed',
        borderColor: 'divider',
        ...(isSelected ? { border: '2px solid', borderColor: 'secondary.main' } : {}) 
      }}
      onClick={() => {
        if (typeof event.VideoOffset === 'number') {
          onSeekToTime(event.VideoOffset / 1000);
        }
      }}
    >
      <Box
        sx={{
          height: 140,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider'
        }}
      >
        <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>
          {label}
        </Typography>
      </Box>
      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
        <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'primary.main', display: 'block', lineHeight: 1.3 }}>
          {formatTime(event.VideoOffset)}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontStyle: 'italic', lineHeight: 1.3 }}>
          Virtual event marker
        </Typography>
      </CardContent>
      {runId && (
        <IconButton
          size="small"
          sx={{ 
            position: 'absolute', 
            bottom: 6, 
            right: 6, 
            bgcolor: hasAnnotations ? 'success.main' : 'background.paper',
            opacity: 0.9,
            '&:hover': { 
              bgcolor: hasAnnotations ? 'success.dark' : 'background.default',
              opacity: 1
            }
          }}
          onClick={(e) => {
            e.stopPropagation();
            onAnnotationClick({ event, clickImageFileName, imageUrl: null });
          }}
        >
          <TagIcon fontSize="small" sx={{ color: hasAnnotations ? 'success.contrastText' : 'text.primary' }} />
        </IconButton>
      )}
    </Card>
  );
};

const ClickImagesView = ({ appInfo, runId = null, viewConfig = {} }) => {
  const { mouseClickRowsWithImages, setSeekTo, selectedEvent, currentVideoTime, allRows, autoinspectData } = useData();

  // helper to extract click image filename is handled inside ClickImageItem where needed
  const [annotationModalOpen, setAnnotationModalOpen] = useState(false);
  const [selectedImageData, setSelectedImageData] = useState(null);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [allAnnotations, setAllAnnotations] = useState([]);
  const [hideIgnored, setHideIgnored] = useState(false);
  const [storageImages, setStorageImages] = useState([]);
  const [loadingStorageImages, setLoadingStorageImages] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Load all annotations for the current run
  React.useEffect(() => {
    if (!runId) {
      setAllAnnotations([]);
      return;
    }

    const annotationsRef = collection(db, 'annotation');
    const q = query(annotationsRef, where('InspectionRunId', '==', runId));

    const unsubscribe = onSnapshot(q, 
      (querySnapshot) => {
        const annotationList = [];
        querySnapshot.forEach((doc) => {
          annotationList.push({
            id: doc.id,
            ...doc.data()
          });
        });
        setAllAnnotations(annotationList);
      },
      (err) => {
        console.error('Error fetching annotations:', err);
        setAllAnnotations([]);
      }
    );

    return () => unsubscribe();
  }, [runId]);

  // List all images from Firebase Storage
  React.useEffect(() => {
    if (!appInfo?.clickimagesPath) {
      setStorageImages([]);
      return;
    }

    const loadStorageImages = async () => {
      setLoadingStorageImages(true);
      try {
        const folderRef = ref(storage, appInfo.clickimagesPath);
        const result = await listAll(folderRef);
        
        // Parse filenames to extract info
        const images = result.items.map(item => {
          const fullName = item.name; // e.g., "251002164749-910m.jpg"
          const fileName = fullName.replace(/\.jpg$/i, ''); // Remove extension
          
          // Check if it's a manual capture (ends with 'm')
          const isManualCapture = fileName.endsWith('m');
          
          // Parse timestamp from filename (YYMMDDHHMMSS-mmm format)
          // Extract VideoOffset by parsing the filename back to a date
          let videoOffset = 0;
          try {
            const baseFileName = isManualCapture ? fileName.slice(0, -1) : fileName;
            // Parse: YYMMDDHHMMSS-mmm
            const match = baseFileName.match(/^(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})-(\d{3})$/);
            if (match) {
              const [, yy, mm, dd, hh, min, ss, ms] = match;
              const year = 2000 + parseInt(yy, 10);
              const imageDate = new Date(Date.UTC(year, parseInt(mm, 10) - 1, parseInt(dd, 10), 
                parseInt(hh, 10), parseInt(min, 10), parseInt(ss, 10), parseInt(ms, 10)));
              
              // Calculate offset from first event if available
              if (allRows && allRows.length > 0) {
                const firstEvent = allRows.find(row => row.ActionTime);
                if (firstEvent) {
                  const firstEventDate = new Date(firstEvent.ActionTime);
                  const firstEventOffset = firstEvent.VideoOffset || 0;
                  const recordingStart = firstEventDate.getTime() - firstEventOffset;
                  videoOffset = imageDate.getTime() - recordingStart;
                }
              }
            }
          } catch (e) {
            // Keep videoOffset as 0 if parsing fails
          }
          
          return {
            clickImageFileName: fileName,
            VideoOffset: videoOffset,
            isManualCapture,
            storagePath: item.fullPath,
          };
        });
        
        setStorageImages(images);
      } catch (err) {
        console.error('Error listing storage images:', err);
        setStorageImages([]);
      } finally {
        setLoadingStorageImages(false);
      }
    };

    loadStorageImages();
  }, [appInfo?.clickimagesPath, allRows, refreshKey]);

  const handleAnnotationClick = (imageData) => {
    setSelectedImageData(imageData);
    setAnnotationModalOpen(true);
  };

  const handleAnnotationClose = () => {
    setAnnotationModalOpen(false);
    setSelectedImageData(null);
  };

  // Helper function to check if an image should be ignored
  const getShouldIgnoreClickImage = useMemo(() => {
    if (!autoinspectData?.verdictsByClickImage) return () => false;
    
    return (clickImageFileName) => {
      if (!clickImageFileName) return false;
      
      // Search through verdictsByClickImage keys to find a match
      // The key might be a full path, so we check if it ends with the filename
      for (const [imagePath, verdict] of Object.entries(autoinspectData.verdictsByClickImage)) {
        const pathFileName = imagePath.split('/').pop().replace(/\.(jpg|png|jpeg)$/i, '');
        if (pathFileName === clickImageFileName || imagePath.endsWith(clickImageFileName)) {
          return verdict.shouldIgnoreClickImage === true;
        }
      }
      return false;
    };
  }, [autoinspectData]);

  // Build click image events from storage listing, enriched with event data where available
  const clickImageEvents = useMemo(() => {
    if (!appInfo || storageImages.length === 0) return [];
    
    // Create a map of event filenames for quick lookup
    const eventsByFilename = new Map();
    if (mouseClickRowsWithImages && Array.isArray(mouseClickRowsWithImages)) {
      mouseClickRowsWithImages.forEach(event => {
        if (event.clickImageFileName) {
          eventsByFilename.set(event.clickImageFileName, event);
        }
      });
    }
    
    // Enrich storage images with event data where available
    const enrichedImages = storageImages.map(storageImg => {
      const matchingEvent = eventsByFilename.get(storageImg.clickImageFileName);
      if (matchingEvent) {
        // Merge storage info with event data
        return {
          ...matchingEvent,
          ...storageImg,
          isManualCapture: storageImg.isManualCapture, // Preserve manual capture flag
        };
      }
      // No matching event - this is a manual capture or orphan image
      return storageImg;
    });
    
    // Sort by VideoOffset
    enrichedImages.sort((a, b) => {
      const aOffset = typeof a.VideoOffset === 'number' ? a.VideoOffset : 0;
      const bOffset = typeof b.VideoOffset === 'number' ? b.VideoOffset : 0;
      return aOffset - bOffset;
    });
    
    // Filter out ignored images if hideIgnored is true
    if (hideIgnored) {
      return enrichedImages.filter(event => !getShouldIgnoreClickImage(event.clickImageFileName));
    }
    
    return enrichedImages;
  }, [storageImages, mouseClickRowsWithImages, appInfo, hideIgnored, getShouldIgnoreClickImage]);

  // Find oldest and youngest events by ActionTime
  const virtualEvents = useMemo(() => {
    if (!allRows || !Array.isArray(allRows) || allRows.length === 0) return { beginning: null, ending: null };

    // Filter events that have ActionTime
    const eventsWithActionTime = allRows.filter(ev => ev.ActionTime != null);
    if (eventsWithActionTime.length === 0) return { beginning: null, ending: null };

    // Sort by ActionTime to find oldest and youngest
    const sorted = eventsWithActionTime.slice().sort((a, b) => {
      const aTime = Date.parse(a.ActionTime);
      const bTime = Date.parse(b.ActionTime);
      if (isNaN(aTime) || isNaN(bTime)) return 0;
      return aTime - bTime;
    });

    const oldest = sorted[0];
    const youngest = sorted[sorted.length - 1];

    // Create virtual event objects
    const beginningEvent = oldest ? {
      ...oldest,
      clickImageFileName: BEGINNING_MARKER,
      VideoOffset: typeof oldest.VideoOffset === 'number' ? oldest.VideoOffset : 0,
      ActionTime: oldest.ActionTime
    } : null;

    const endingEvent = youngest ? {
      ...youngest,
      clickImageFileName: ENDING_MARKER,
      VideoOffset: typeof youngest.VideoOffset === 'number' ? youngest.VideoOffset : 0,
      ActionTime: youngest.ActionTime
    } : null;

    return { beginning: beginningEvent, ending: endingEvent };
  }, [allRows]);

  // Early return if appInfo is not available yet
  if (!appInfo) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Loading app information...
        </Typography>
      </Box>
    );
  }

  // Helper to check if an event is selected
  const checkIsSelected = (event) => {
    const windowMs = 200; // tolerance in milliseconds
    let referenceTimeMs = null;
    if (selectedEvent && typeof selectedEvent.VideoOffset === 'number') {
      referenceTimeMs = selectedEvent.VideoOffset;
    } else if (typeof currentVideoTime === 'number') {
      // currentVideoTime is in seconds in the provider; convert to ms
      referenceTimeMs = Math.round(currentVideoTime * 1000);
    }

    return referenceTimeMs !== null && typeof event.VideoOffset === 'number'
      ? Math.abs(event.VideoOffset - referenceTimeMs) <= windowMs
      : false;
  };

  const totalItems = (virtualEvents.beginning ? 1 : 0) + clickImageEvents.length + (virtualEvents.ending ? 1 : 0);

  if (loadingStorageImages) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Loading images from storage...
        </Typography>
      </Box>
    );
  }

  if (totalItems === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No click images found in storage.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.75 }}>
        <Checkbox
          checked={hideIgnored}
          onChange={(e) => setHideIgnored(e.target.checked)}
          size="small"
          sx={{ p: 0 }}
        />
        <Typography variant="body2" component="span" sx={{ fontSize: 11 }}>
          Hide ignored
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto', fontSize: 11 }}>
          {totalItems} images
        </Typography>
        {!!viewConfig?.refreshClickImages && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
            onClick={() => setRefreshKey(k => k + 1)}
            disabled={loadingStorageImages}
            sx={{ ml: 0.5, fontSize: 11, py: 0, px: 1, minHeight: 22 }}
          >
            Refresh
          </Button>
        )}
      </Box>
      
      <Grid container spacing={2}>
        {/* Beginning virtual item */}
        {virtualEvents.beginning && (
          <Grid item key="beginning">
            <VirtualEventItem
              event={virtualEvents.beginning}
              label="Beginning"
              onSeekToTime={setSeekTo}
              onAnnotationClick={handleAnnotationClick}
              runId={runId}
              allAnnotations={allAnnotations}
              isSelected={checkIsSelected(virtualEvents.beginning)}
            />
          </Grid>
        )}

        {/* Regular click image events */}
        {clickImageEvents.map((event, index) => {
          const shouldIgnore = getShouldIgnoreClickImage(event.clickImageFileName);
          return (
            <Grid item key={event.id || `event-${index}`}>
              <ClickImageItem
                event={event}
                appInfo={appInfo}
                onSeekToTime={setSeekTo}
                onAnnotationClick={handleAnnotationClick}
                onFullscreenClick={setFullscreenImage}
                runId={runId}
                allAnnotations={allAnnotations}
                isSelected={checkIsSelected(event)}
                shouldIgnore={shouldIgnore}
                isManualCapture={event.isManualCapture === true}
              />
            </Grid>
          );
        })}

        {/* Ending virtual item */}
        {virtualEvents.ending && (
          <Grid item key="ending">
            <VirtualEventItem
              event={virtualEvents.ending}
              label="Ending"
              onSeekToTime={setSeekTo}
              onAnnotationClick={handleAnnotationClick}
              runId={runId}
              allAnnotations={allAnnotations}
              isSelected={checkIsSelected(virtualEvents.ending)}
            />
          </Grid>
        )}
      </Grid>
      
      <ImageAnnotation
        open={annotationModalOpen}
        onClose={handleAnnotationClose}
        imageData={selectedImageData}
        appInfo={appInfo}
        runId={runId}
        allAnnotations={allAnnotations}
        viewConfig={viewConfig}
      />

      {/* Fullscreen image preview */}
      <Dialog
        open={!!fullscreenImage}
        onClose={() => setFullscreenImage(null)}
        fullScreen
        slotProps={{ paper: { sx: { bgcolor: 'rgba(0,0,0,0.92)', cursor: 'pointer' }, onClick: () => setFullscreenImage(null) } }}
      >
        {fullscreenImage?.imageUrl && (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
            <Box
              component="img"
              src={fullscreenImage.imageUrl}
              alt={fullscreenImage.clickImageFileName || 'Full screen preview'}
              sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          </Box>
        )}
      </Dialog>
    </Box>
  );
};

export default ClickImagesView;
